import { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('holder-language');
    return saved || 'pt';
  });

  useEffect(() => {
    localStorage.setItem('holder-language', language);
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && value[k]) {
        value = value[k];
      } else {
        return key;
      }
    }
    
    return value;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const translations = {
  pt: {
    nav: {
      home: 'Início',
      about: 'Sobre',
      services: 'Serviços',
      partners: 'Parceiros',
      contact: 'Contato',
      allServices: 'Todos os Serviços',
      access: 'Acessos',
      contactUs: 'Fale Conosco',
    },
    services: {
      fiscalManagement: 'Gestão Fiscal Digital',
      fiscalManagementDesc: 'Identificação de créditos fiscais e regularização de passivos tributários.',
      positiveTax: 'Gestão Positiva de Tributos',
      positiveTaxDesc: 'Economia tributária através de direitos creditórios de origem financeira.',
      liabilities: 'Gestão de Passivos Fiscais',
      liabilitiesDesc: 'Regularização fiscal com negociação de descontos e parcelamentos.',
      ma: 'M&A - Fusões e Aquisições',
      maDesc: 'Assessoria completa em operações de compra, venda e fusão de empresas.',
      credit: 'Crédito Estruturado',
      creditDesc: 'Financiamento customizado para atender demandas específicas do negócio.',
      legal: 'Assessoria Jurídica Empresarial',
      legalDesc: 'Suporte legal estratégico para proteger o patrimônio e garantir conformidade.',
    },
    home: {
      heroTag: 'Boutique de Soluções Corporativas',
      heroTitle: 'Soluções inteligentes para',
      heroHighlight: 'transformar',
      heroTitleEnd: 'seu negócio',
      heroDesc: 'Um ecossistema de soluções integradas para financiar operações, gerir impostos, otimizar pessoas e transformar negócios.',
      scheduleBtn: 'Agendar Reunião',
      ourServices: 'Nossos Serviços',
      statsAssets: 'Em ativos gerenciados',
      statsPartners: 'Instituições parceiras',
      statsAgents: 'Agentes em todo Brasil',
      statsExperience: 'Anos de experiência',
      aboutUs: 'Sobre Nós',
      aboutTitle: 'Especialistas em desenvolvimento financeiro e empresarial',
      aboutDesc: 'A Holder & Co. Group é uma boutique de soluções corporativas fundada por profissionais com mais de 20 anos de expertise no mercado. Nosso ecossistema integrado oferece desde consultoria estratégica até soluções financeiras digitais.',
      aboutCredits: 'Mais de R$ 1,2 bilhão em créditos fiscais identificados',
      aboutCompliance: 'Conformidade total com LGPD e normas regulatórias',
      aboutNational: 'Abrangência nacional com equipe especializada',
      knowHistory: 'Conheça Nossa História',
      servicesTag: 'Nossos Serviços',
      servicesTitle: 'Soluções completas para sua empresa',
      servicesDesc: 'Do planejamento estratégico à execução, oferecemos um portfólio abrangente de serviços para impulsionar o crescimento do seu negócio.',
      learnMore: 'Saiba mais',
      viewAll: 'Ver Todos os Serviços',
      trustedBy: 'EMPRESAS QUE CONFIAM EM NOSSO TRABALHO',
      mediaTag: 'Acompanhe',
      mediaTitle: 'Holder & CO. na Mídia',
      mediaDesc: 'Fique por dentro das novidades, conteúdos exclusivos e participações na mídia.',
      followInstagram: 'Siga-nos no Instagram',
      youtubeTitle: 'Matérias no YouTube',
      youtubeVideo1: 'HAS - Hold Account Service na Mídia',
      youtubeVideo2: 'HAS - Soluções Financeiras Digitais',
      ctaTag: 'Vamos Conversar',
      ctaTitle: 'Pronto para dar o próximo passo?',
      ctaDesc: 'Agende uma reunião gratuita com nossos especialistas e descubra como podemos ajudar sua empresa a alcançar novos patamares.',
      ctaBtn: 'Agendar Reunião Gratuita',
      yearsExp: 'Anos de Experiência',
      unitsInBrazil: 'Unidades no Brasil',
    },
    footer: {
      ctaTitle: 'Pronto para transformar seu negócio?',
      ctaDesc: 'Agende uma reunião com nossos especialistas e descubra como podemos ajudar sua empresa a crescer.',
      ctaBtn: 'Agendar Reunião',
      about: 'Boutique de soluções corporativas especializada em desenvolvimento financeiro e empresarial.',
      services: 'Serviços',
      quickLinks: 'Links Rápidos',
      contact: 'Contato',
      phone: 'Telefone',
      email: 'E-mail',
      offices: 'Nossas Unidades',
      rights: 'Todos os direitos reservados.',
      compliance: 'Em conformidade com a LGPD nº 13.709/2018',
      aboutUs: 'Sobre Nós',
      partnerProgram: 'Programa de Parceiros',
    },
    about: {
      historyTag: 'Nossa História',
      historyTitle: 'Construindo o futuro financeiro desde',
      historyDesc1: 'A Holder & Co. Group nasceu com o propósito de democratizar o acesso a soluções financeiras de alto nível. Somos uma boutique de soluções corporativas que combina experiência de mercado com inovação tecnológica.',
      historyDesc2: 'Nossa equipe de especialistas possui mais de 20 anos de expertise no mercado financeiro, tendo participado da avaliação, recuperação, renegociação, captação e gestão de mais de R$ 2 bilhões em ativos e recursos.',
      missionTitle: 'Nossa Missão',
      missionDesc: 'Divulgar e intermediar a contratação de produtos e serviços digitais de meios de pagamento, otimizando a gestão financeira e transações de clientes corporativos. Proporcionar resultados sólidos e sustentáveis, entregando soluções personalizadas focadas no sucesso de cada cliente.',
      visionTitle: 'Nossa Visão',
      visionDesc: 'Ser reconhecida como a principal referência em soluções corporativas integradas no Brasil, transformando a forma como empresas gerenciam suas finanças, tributos e operações estratégicas, sempre com excelência e inovação.',
      valuesTag: 'O que nos guia',
      valuesTitle: 'Nossos Valores',
      excellence: 'Excelência',
      excellenceDesc: 'Comprometimento com os mais altos padrões de qualidade em tudo o que fazemos.',
      innovation: 'Inovação',
      innovationDesc: 'Busca constante por soluções criativas e tecnologias de ponta.',
      security: 'Segurança',
      securityDesc: 'Proteção total dos dados e operações de nossos clientes.',
      compliance: 'Conformidade',
      complianceDesc: 'Atuação em total conformidade com leis e regulamentações.',
      clientFocus: 'Foco no Cliente',
      clientFocusDesc: 'Soluções personalizadas para as necessidades de cada cliente.',
      ecosystemTag: 'Nosso Ecossistema',
      ecosystemTitle: 'Duas Marcas, Uma Solução Completa',
      holderBrand: 'Consultoria Estratégica',
      hasBrand: 'Hold Account Service - Soluções Financeiras Digitais',
      teamTag: 'Quem Somos',
      teamTitle: 'Nossa Equipe',
      teamDesc: 'Profissionais experientes e comprometidos com a excelência no atendimento aos nossos clientes.',
      officesTag: 'Presença Nacional',
      officesTitle: 'Nossas Unidades',
      officesDesc: 'Com unidades estrategicamente localizadas, estamos prontos para atender empresas em todo o território nacional.',
      agentsInfo: 'Mais de 100 Agentes Holder em todo o Brasil',
      ctaTitle: 'Faça parte da nossa história',
      ctaDesc: 'Descubra como podemos ajudar sua empresa a alcançar novos patamares.',
      ctaBtn: 'Entre em Contato',
      holderServices: {
        credit: 'Crédito Estruturado',
        ma: 'Fusões e Aquisições (M&A)',
        consulting: 'Consultoria de Gestão e Tecnologia',
        fiscal: 'Gestão Fiscal e Contábil',
        legal: 'Assessoria Jurídica Empresarial',
      },
      hasServices: {
        accounts: 'Contas Digitais PF e PJ',
        escrow: 'Escrow Account',
        payments: 'Meios de Pagamento',
        cards: 'Máquinas de Cartões',
        marketplace: 'Marketplace Financeiro',
      },
      team: {
        erico: {
          name: 'Erico Comassetto Sperb',
          role: 'Diretor de Relações Institucionais',
          desc: 'Advogado e empresário, especialista em Direito Tributário Empresarial, com sólida experiência em expansão de redes e coordenação de equipes de trabalho.',
        },
        nelson: {
          name: 'Nelson Gilberto Campos Feijó',
          role: 'Diretor Jurídico',
          desc: 'Com mais de 30 anos de atuação na área jurídica, marcados pela experiência em negociações administrativas tributárias em nível estadual.',
        },
        renan: {
          name: 'Renan Mensch Scalzilli',
          role: 'Diretor de Operações',
          desc: 'Administrador pela UFRGS com ênfase em Finanças Internacionais, possui sólida experiência com gestão de stress, turnaround e M&A.',
        },
        paulo: {
          name: 'Paulo Cesar Pooch Ferreira',
          role: 'Diretor de Tecnologia',
          desc: 'Graduado em Administração de Empresas pela PUCRS. Experiência sólida em análise de dados, análise financeira e desenvolvimento de sistemas.',
        },
        kamila: {
          name: 'Kamila Arpini Campos Feijó',
          role: 'Analista Jurídica',
          desc: 'Especialista em suporte jurídico e análise de processos tributários.',
        },
        tyler: {
          name: 'Tyler Brito',
          role: 'Analista Comercial',
          desc: 'Responsável pelo desenvolvimento de negócios e relacionamento com clientes.',
        },
      },
      offices: {
        matriz: 'Matriz',
        unit: 'Unidade',
      },
    },
    contact: {
      tag: 'Contato',
      title: 'Vamos',
      titleHighlight: 'conversar',
      desc: 'Entre em contato conosco para agendar uma reunião ou tirar suas dúvidas. Nossa equipe está pronta para ajudar sua empresa.',
      infoTitle: 'Informações de Contato',
      phone: 'Telefone',
      email: 'E-mail',
      hours: 'Horário de Atendimento',
      hoursValue: 'Segunda a Sexta: 9h às 18h',
      offices: 'Nossas Unidades',
      social: 'Redes Sociais',
      formTitle: 'Envie uma Mensagem',
      formDesc: 'Preencha o formulário abaixo e retornaremos em até 24 horas.',
      name: 'Nome Completo',
      emailField: 'E-mail',
      phoneField: 'Telefone',
      company: 'Empresa',
      subject: 'Assunto',
      subjectPlaceholder: 'Selecione um assunto',
      message: 'Mensagem',
      submit: 'Enviar Mensagem',
      sending: 'Enviando...',
      successTitle: 'Mensagem Enviada!',
      successDesc: 'Obrigado pelo contato. Nossa equipe retornará em breve.',
      newMessage: 'Enviar Nova Mensagem',
      privacy: 'Ao enviar, você concorda com nossa política de privacidade e tratamento de dados conforme LGPD.',
      errorTitle: 'Erro ao enviar',
      errorDesc: 'Por favor, tente novamente ou entre em contato por telefone.',
      subjects: {
        ma: 'M&A - Fusões e Aquisições',
        fiscal: 'Gestão Fiscal Digital',
        positive: 'Gestão Positiva de Tributos',
        liabilities: 'Gestão de Passivos',
        credit: 'Crédito Estruturado',
        partners: 'Programa de Parceiros',
        legal: 'Assessoria Jurídica',
        other: 'Outro assunto',
      },
    },
    partners: {
      tag: 'Programa de Parceiros',
      title: 'Seja um',
      titleHighlight: 'Agente Holder',
      desc: 'Faça parte de uma rede com mais de 100 agentes em todo o Brasil. Indique clientes, feche negócios e receba comissões atrativas em um modelo transparente e meritocrático.',
      cta: 'Quero Ser Parceiro',
      tiersTag: 'Faixas de Parceria',
      tiersTitle: 'Escolha Sua Faixa',
      adhesion: 'Adesão',
      free: 'Gratuita',
      optional: '(opcional)',
      mostPopular: 'Mais Popular',
      benefit1Bronze: 'Acesso ao programa de parceria',
      benefit2Bronze: 'Comissionamento base',
      benefit3Bronze: 'Suporte comercial',
      benefit4Bronze: 'Material de vendas',
      benefit1Silver: 'Todos benefícios Bronze',
      benefit2Silver: 'Comissionamento ampliado',
      benefit3Silver: 'Suporte prioritário',
      benefit4Silver: 'Treinamentos exclusivos',
      benefit1Gold: 'Todos benefícios Silver',
      benefit2Gold: 'Máximo comissionamento',
      benefit3Gold: 'Gerente de contas dedicado',
      benefit4Gold: 'Eventos VIP',
      wantToBe: 'Quero Ser',
      ascensionTag: 'Crescimento',
      ascensionTitle: 'Como Subir de Faixa',
      rule1Title: 'Indicação Simples',
      rule1Desc: 'Indique 5 parceiros Bronze que fechem negócio para subir para Silver. Para Gold, indique 5 Silver.',
      rule2Title: 'Compra de Faixa',
      rule2Desc: 'Acelere sua mudança de faixa adquirindo a adesão correspondente (Silver: R$ 50k, Gold: R$ 100k).',
      rule3Title: 'Faturamento Próprio',
      rule3Desc: 'Bronze para Silver: R$ 100k em vendas. Silver para Gold: R$ 500k em vendas.',
      ctaTitle: 'Pronto para começar?',
      ctaDesc: 'Entre em contato conosco e comece sua jornada como Agente Holder hoje mesmo.',
    },
    access: {
      title: 'Escolha o Portal',
      portalHolder: 'Portal Holder',
      portalHolderDesc: 'Acesse o portal de clientes',
      hasBanking: 'Internet Banking HAS',
      hasBankingDesc: 'Acesse sua conta digital',
    },
    servicesPage: {
      heroTag: 'Nossos Serviços',
      heroTitle: 'Soluções completas para',
      heroHighlight: 'cada necessidade',
      heroDesc: 'Um ecossistema de soluções integradas que combina consultoria estratégica de alto nível com tecnologia financeira de ponta.',
      highlightsTag: 'Destaques',
      highlightsTitle: 'Serviços Especializados',
      viewDetails: 'Ver detalhes',
      consultingTag: 'Holder & Co.',
      consultingTitle: 'Consultoria Estratégica',
      digitalTag: 'HAS - Hold Account Service',
      digitalTitle: 'Soluções Financeiras Digitais',
      techTag: 'Tecnologia',
      techTitle: 'Integração Tecnológica Avançada',
      techDesc: 'Oferecemos APIs robustas para integração com sistemas de gestão empresarial, fintechs e plataformas de parceiros, permitindo automação de processos financeiros e maior eficiência operacional.',
      techFeature1: 'APIs Financeiras de alta performance',
      techFeature2: 'Compatibilidade com principais ERPs do mercado',
      techFeature3: 'Ecossistema de soluções integradas',
      techFeature4: 'Automação de processos financeiros',
      techCode: 'Documentação completa e suporte técnico dedicado para sua integração.',
      ctaTitle: 'Não encontrou o que procura?',
      ctaDesc: 'Desenvolvemos projetos sob medida para atender necessidades específicas do seu negócio.',
      ctaBtn: 'Fale com um Especialista',
      consulting: {
        management: 'Consultoria de Gestão e Tecnologia',
        managementDesc: 'Implementação de soluções que otimizam processos e impulsionam o crescimento empresarial.',
        accounting: 'Gestão Contábil',
        accountingDesc: 'Planejamento e controle para maximizar eficiência tributária e contábil.',
        legal: 'Assessoria Jurídica Empresarial',
        legalDesc: 'Suporte legal estratégico para proteger o patrimônio e garantir conformidade.',
        thirdSector: 'Soluções para o Terceiro Setor',
        thirdSectorDesc: 'Consultoria especializada em soluções de impacto social através de organizações sem fins lucrativos.',
      },
      digital: {
        accounts: 'Contas Digitais',
        accountsDesc: 'Conta completa com cartão de débito e crédito para PF e PJ.',
        escrow: 'Escrow Account',
        escrowDesc: 'Conta de garantia para transações seguras com liberação condicional.',
        payments: 'Meios de Pagamento',
        paymentsDesc: 'Cartões, boleto, transferências, carteiras digitais e split de pagamentos.',
        baas: 'Banking as a Service (BaaS)',
        baasDesc: 'Flexibilidade para oferecer produtos financeiros sem infraestrutura bancária própria.',
        boletos: 'Emissão de Boletos',
        boletosDesc: 'Sistema integrado para geração e controle de boletos bancários.',
        transfers: 'Transferências (TED, Pix)',
        transfersDesc: 'Soluções rápidas e seguras para transferências financeiras.',
        exchange: 'Câmbio',
        exchangeDesc: 'Acesso às melhores taxas de câmbio para operações internacionais.',
        creditFinancing: 'Crédito e Financiamento',
        creditFinancingDesc: 'Linhas de financiamento e empréstimos com condições especiais.',
        insurance: 'Seguros e Consórcios',
        insuranceDesc: 'Soluções de proteção e alternativas de aquisição de bens.',
      },
    },
    common: {
      learnMore: 'Saiba mais',
      scheduleMeeting: 'Agendar Reunião',
      contactUs: 'Entre em Contato',
      viewAll: 'Ver Todos',
    },
    maPage: {
      heroTag: 'Fusões & Aquisições',
      heroTitle: 'Assessoria completa em',
      heroHighlight: 'M&A',
      heroDesc: 'Conduzimos todo o processo de venda, desde a concepção até a materialidade, com expertise em valuation, due diligence e negociação estratégica.',
      ctaPrimary: 'Agendar Diagnóstico Gratuito',
      statsOperations: 'Em operações assessoradas',
      statsPartners: 'Instituições parceiras',
      statsProjects: 'De projetos realizados',
      statsExperience: 'Anos de experiência',
      methodologyTag: 'Metodologia',
      methodologyTitle: 'Nosso Processo de M&A',
      step1: 'Teaser',
      step1Desc: 'Apresentação inicial do negócio para potenciais investidores.',
      step2: 'Avaliação de Cenários',
      step2Desc: 'Análise estratégica das opções e caminhos possíveis.',
      step3: 'Negociação',
      step3Desc: 'Condução das tratativas com compradores qualificados.',
      step4: 'Captação',
      step4Desc: 'Busca ativa de investidores e fechamento do deal.',
      servicesTag: 'Serviços em M&A',
      servicesTitle: 'Assessoria Completa',
      service1: 'Aproximação de potenciais Buyers',
      service2: 'Contrato de Confidencialidade (NDA)',
      service3: 'Negociação e Due Diligence',
      service4: 'Carta de Intenções (LOI)',
      service5: 'SPA (Share Purchase Agreement)',
      service6: 'Closing e Transferência',
      valuationTag: 'Valuation',
      valuationTitle: 'Avaliação Econômico-Financeira',
      valuationDesc: 'Utilizamos práticas de mercado e um processo metodológico criterioso para realizar a avaliação financeira do seu negócio.',
      valuationStepsTitle: 'Etapas do Valuation',
      valuationStep1: 'Diagnóstico Interno',
      valuationStep2: 'Diagnóstico Externo',
      valuationStep3: 'Projeções Financeiras',
      valuationStep4: 'Avaliação de Necessidade',
      valuationStep5: 'Valor Final',
      creditTag: 'Crédito e Operações',
      creditTitle: 'Operações Estruturadas',
      creditDesc: 'Relacionamento com mais de 200 instituições entre bancos, fundos, FIDCs, credores e investidores para atender qualquer demanda por recursos.',
      credit1: 'Capital de Giro',
      credit1Desc: 'Recursos para operação do dia a dia',
      credit2: 'Garantia de Recebíveis',
      credit2Desc: 'Crédito com lastro em recebíveis',
      credit3: 'Garantia de Veículos',
      credit3Desc: 'Financiamento com frota como garantia',
      credit4: 'Máquinas e Equipamentos',
      credit4Desc: 'Crédito para aquisição de bens',
      credit5: 'Operação de Mezanino',
      credit5Desc: 'Financiamento híbrido',
      credit6: 'Incentivos Estatais',
      credit6Desc: 'Acesso a linhas subsidiadas',
      networkTag: 'Nossa Rede',
      networkTitle: 'Acesso a Investidores Qualificados',
      networkDesc: 'Desenvolvemos a Tese de Investimento do seu negócio e prospectamos investidores através da nossa rede exclusiva, que inclui centenas de investidores estratégicos, fundos de private equity, venture capital e family offices.',
      privateEquity: 'Private Equity',
      ventureCapital: 'Venture Capital',
      familyOffices: 'Family Offices',
      strategicInvestors: 'Investidores Estratégicos',
      diagnosisTitle: 'Diagnóstico Gratuito',
      diagnosisDesc: 'Agende uma reunião para um diagnóstico gratuito de potencial de valor do seu negócio.',
      diagnosisBtn: 'Agendar Diagnóstico',
      ctaTitle: 'Pronto para dar o próximo passo?',
      ctaDesc: 'Converse com nossa equipe e entenda como podemos ajudar no seu caso específico.',
      ctaBtn: 'Agendar Reunião',
    },
    gfdPage: {
      heroTag: 'Gestão Fiscal Digital',
      heroTitle: 'Transforme complexidade tributária em',
      heroHighlight: 'fluxo de caixa positivo',
      heroDesc: 'Uma investigação estratégica dos últimos 60 meses da sua empresa para identificar créditos ocultos, mapear riscos e otimizar seu caixa.',
      ctaPrimary: 'Solicitar Diagnóstico',
      statsCredits: 'Em créditos fiscais identificados',
      statsMonths: 'Meses de análise retroativa',
      statsExperience: 'Anos de experiência',
      statsDays: 'Dias úteis para entrega',
      approachTag: 'Nossa Abordagem',
      approachTitle: 'Os Três Pilares da GFD',
      approachDesc: 'A Gestão Fiscal Digital vai além da contabilidade tradicional, realizando um raio-x completo da sua situação tributária.',
      pillar1: 'REVISE',
      pillar1Sub: 'Ativos Ocultos',
      pillar1Desc: 'Créditos fiscais federais, estaduais e municipais que podem ser recuperados administrativamente.',
      pillar2: 'RECUPERE',
      pillar2Sub: 'Otimização de Caixa',
      pillar2Desc: 'Alternativas legais para antecipar créditos e reduzir o passivo fiscal.',
      pillar3: 'REGULARIZE',
      pillar3Sub: 'Segurança (Passivos)',
      pillar3Desc: 'Identificação de falhas de preenchimento e riscos de autuação antes que o fisco os identifique.',
      methodologyTag: 'Metodologia',
      methodologyTitle: 'O Caminho do Sucesso em 6 Passos',
      method1: 'Coleta Digital',
      method1Desc: 'Captura de dados via procuração eletrônica (e-CAC) ou compartilhamento de arquivos.',
      method2: 'Identificação',
      method2Desc: 'Cruzamento de dados utilizando softwares avançados para detectar inconsistências.',
      method3: 'Mapeamento',
      method3Desc: 'Análise detalhada dos impactos tributários por consultores especializados.',
      method4: 'Reunião Técnica',
      method4Desc: 'Discussão com a equipe da empresa sobre os pontos identificados.',
      method5: 'Dossiê Final',
      method5Desc: 'Apresentação à diretoria com memórias de cálculo e suporte para decisão.',
      method6: 'Habilitação',
      method6Desc: 'Orientação técnica para recuperação dos ativos e retificações necessárias.',
      feeTag: 'Modelo de Contratação',
      feeTitle: 'Risco Zero (Success Fee)',
      feeDesc: 'Nosso modelo de remuneração é baseado exclusivamente no sucesso. Se não houver benefício financeiro identificado, não há custo para você.',
      feeAssets: 'Honorários sobre Ativos',
      feeAssetsDesc: 'sobre benefícios fiscais identificados',
      feeLiabilities: 'Honorários sobre Passivos',
      feeLiabilitiesDesc: 'sobre riscos/passivos mapeados',
      guaranteesTitle: 'Garantias Inclusas',
      guarantee1: 'Sem custo se não houver benefício identificado',
      guarantee2: 'Despesas de deslocamento por nossa conta',
      guarantee3: 'Entrega em até 60 dias úteis',
      guarantee4: 'Seguro de responsabilidade civil',
      securityTag: 'Segurança',
      securityTitle: 'Segurança e Compliance',
      lgpd: 'LGPD',
      lgpdDesc: 'Tratamento de dados estritamente para o objeto do contrato, com descarte total após o término.',
      insurance: 'Seguro Profissional',
      insuranceDesc: 'Apólice de responsabilidade civil junto à AKAD Seguros para total tranquilidade.',
      foundation: 'Fundamentação',
      foundationDesc: 'Relatórios baseados em instruções normativas e julgados atuais da Receita Federal.',
      urgencyTitle: 'O Tempo é Seu Ativo',
      urgencyDesc: 'O fisco tem um prazo de 5 anos para analisar. Cada mês que passa representa um crédito que pode prescrever.',
      urgencyBtn: 'Solicitar Análise Agora',
    },
    gptPage: {
      heroTag: 'Gestão Positiva de Tributos',
      heroTitle: 'Economia tributária através de',
      heroHighlight: 'direitos creditórios',
      heroDesc: 'Utilize créditos de decisões judiciais transitadas em julgado para quitar tributos federais como PIS-COFINS, CSLL e IRPJ, desbloqueando até 20% de economia recorrente.',
      ctaPrimary: 'Agendar Reunião',
      accountingTitle: 'Tratamento Contábil',
      asset: 'ATIVO (100%)',
      assetDesc: 'Direitos Creditórios Adquiridos',
      liability: 'PASSIVO (80%)',
      liabilityDesc: 'Contas a Pagar',
      revenue: 'RECEITAS A REALIZAR (20%)',
      revenueDesc: 'Economia Tributária',
      whatIsTag: 'O que é',
      whatIsTitle: 'Direitos Creditórios de Origem Financeira (DCOF)',
      whatIsDesc: 'Créditos provenientes de decisões judiciais contra a União, com trânsito em julgado, que podem ser adquiridos pela empresa e utilizados para quitar tributos federais.',
      whatIsHighlight: 'Uma estratégia tributária antes restrita a grandes players, agora acessível para empresas de médio e grande porte.',
      whatIsNotTitle: 'O que NÃO é a Gestão Positiva',
      notTax: 'Tese Jurídica',
      notTaxDesc: 'Não se baseia em interpretações questionáveis',
      notBenefit: 'Benefício Fiscal',
      notBenefitDesc: 'Não é isenção concedida pelo governo',
      notCompensation: 'Compensação Tradicional',
      notCompensationDesc: 'Diferente das compensações comuns',
      notCredit: 'Crédito Tributário',
      notCreditDesc: 'É direito creditório de origem financeira',
      benefitsTag: 'Benefícios',
      benefitsTitle: 'Vantagens da Gestão Positiva',
      benefit1: 'Economia Recorrente',
      benefit1Desc: 'Até 20% de economia recorrente nos tributos federais.',
      benefit2: 'Segurança Jurídica',
      benefit2Desc: 'Créditos com trânsito em julgado e parecer técnico especializado.',
      benefit3: 'Melhoria de Caixa',
      benefit3Desc: 'Injeção direta de dinheiro no caixa da empresa.',
      benefit4: 'Governança Contábil',
      benefit4Desc: 'Registro adequado no balanço patrimonial e na EFD/ECF.',
      creditUsage: 'Utilize o crédito ao longo de 10 meses, conforme seu fluxo de caixa.',
      processTag: 'Passo a Passo',
      processTitle: 'Como Funciona',
      process1: 'Cessão do DCOF',
      process1Desc: 'Contrato de cessão de direito firmado por duas testemunhas.',
      process2: 'Protocolização',
      process2Desc: 'Protocolização imediata no processo judicial digital.',
      process3: 'Comunicação Administrativa',
      process3Desc: 'Realizada na 1ª obrigação acessória (EFD) após a cessão.',
      process4: 'Balanço Patrimonial',
      process4Desc: 'Crédito registrado no balanço patrimonial do contribuinte.',
      process5: 'Declaração ECF',
      process5Desc: 'Utilizações do crédito declaradas na ECF anual.',
      process6: 'Responsabilidade Técnica',
      process6Desc: 'Responsabilidade técnica por 5 anos (jurídico, contábil, fiscal).',
      legalTag: 'Fundamentação',
      legalTitle: 'Base Legal',
      legal1: 'EC nº 113/2021',
      legal1Date: '8 de dezembro de 2021',
      legal1Desc: 'Novo regime de precatórios, permitindo uso de créditos para quitar débitos com a União.',
      legal2: 'IN RFB nº 2055/2021',
      legal2Date: '8 de dezembro de 2021',
      legal2Desc: 'Regulamenta restituição, compensação e ressarcimento de tributos administrados pela Receita Federal.',
      legal3: 'Decreto nº 11.526/2023',
      legal3Date: '12 de maio de 2023',
      legal3Desc: 'Ajusta procedimentos para maior segurança jurídica na oferta de créditos.',
      securityTag: 'Segurança',
      securityTitle: 'Abordagem Jurídica-Contábil-Fiscal Integrada',
      securityDesc: 'Nossa operação é conduzida com total segurança jurídica, garantindo homologação com a PGFN e proteção contra riscos de desconstituição.',
      security1: 'Créditos com trânsito em julgado',
      security2: 'Parecer técnico especializado',
      security3: 'Homologação com a PGFN',
      security4: 'Garantia CPR para defesa judicial',
      studyTitle: 'Estudo de Caso Personalizado',
      studyDesc: 'Quantifique o potencial de economia específico para sua empresa e seu impacto no resultado.',
      studyBtn: 'Solicitar Estudo',
      ctaTitle: 'Transforme Sua Gestão Fiscal',
      ctaDesc: 'Descubra como a economia tributária via direito creditório pode otimizar sua gestão fiscal e desempenho financeiro.',
      ctaBtn: 'Agendar Reunião',
    },
    gpPage: {
      heroTag: 'Gestão de Passivos Fiscais',
      heroTitle: 'Regularização fiscal com',
      heroHighlight: 'descontos significativos',
      heroDesc: 'Transforme dívidas tributárias em oportunidades através da revisão de capacidade de pagamento, negociação de descontos de até 70% e parcelamentos em até 144 meses.',
      ctaPrimary: 'Analisar Minha Dívida',
      caseStudyTag: 'Estudo de Caso Real',
      caseInitialDebt: 'Dívida Inicial',
      caseInitialRating: 'Rating Inicial',
      caseDiscount: 'Desconto Obtido',
      caseFinalValue: 'Valor Final',
      caseInstallments: 'Parcelas de apenas R$ 4.000/mês em mais de 10 anos',
      ratingTag: 'Sistema de Rating',
      ratingTitle: 'Quanto Menor o Rating, Maior o Desconto',
      ratingDesc: 'A classificação (rating) determina as condições de negociação. Nosso trabalho é analisar sua real capacidade de pagamento e buscar a reclassificação para obter melhores condições.',
      ratingA: 'Rating muito alto',
      ratingB: 'Desconto moderado',
      ratingC: 'Desconto significativo',
      ratingD: 'Máximo desconto',
      installmentsInfo: 'Parcelamento em até 144 meses',
      processTag: 'Metodologia',
      processTitle: 'Nosso Processo de Regularização',
      process1: 'Análise do CAPAG',
      process1Desc: 'Avaliação da capacidade de pagamento calculada pelo fisco vs. a realidade da empresa.',
      process2: 'Laudo de Revisão',
      process2Desc: 'Elaboração de laudo técnico para revisão da capacidade de pagamento.',
      process3: 'Protocolo na PGFN',
      process3Desc: 'Submissão do requerimento junto à Procuradoria-Geral da Fazenda Nacional.',
      process4: 'Negociação',
      process4Desc: 'Condução das tratativas para obter melhores condições de parcelamento.',
      benefitsTag: 'Benefícios',
      benefitsTitle: 'Por que Regularizar Seus Passivos?',
      benefit1: 'Redução significativa do valor total da dívida',
      benefit2: 'Condições de pagamento mais acessíveis e flexíveis',
      benefit3: 'Liberação de fluxo de caixa para operações',
      benefit4: 'Evita sanções e possíveis bloqueios',
      benefit5: 'Regularização fiscal completa',
      legalBasisTitle: 'Base Legal',
      legalBasisDesc: 'Nossa atuação é fundamentada na Portaria PGFN nº 6.757/2022, que estabelece as premissas para negociação de dívidas tributárias.',
      legalBasisNote: 'A capacidade de pagamento calculada pelo fisco pode ser maior que a realidade da sua empresa. Nosso laudo técnico demonstra sua real situação.',
      comparisonTag: 'Comparativo',
      comparisonTitle: 'Antes e Depois da Regularização',
      withoutTitle: 'Sem Gestão de Passivos',
      without1: 'Dívida integral sem descontos',
      without2: 'Parcelas elevadas',
      without3: 'Risco de sanções e bloqueios',
      without4: 'Comprometimento do fluxo de caixa',
      without5: 'Dificuldade para obter crédito',
      withTitle: 'Com Gestão de Passivos',
      with1: 'Descontos de até 70% na dívida',
      with2: 'Parcelas adequadas à capacidade',
      with3: 'Regularização fiscal completa',
      with4: 'Liberação de fluxo de caixa',
      with5: 'Melhora na análise de crédito',
      ctaTitle: 'Descubra Seu Potencial de Desconto',
      ctaDesc: 'Analisamos sua situação fiscal e identificamos as melhores oportunidades de negociação para sua empresa.',
      ctaBtn: 'Solicitar Análise Gratuita',
    },
    creditPage: {
      heroTag: 'Crédito Estruturado',
      heroTitle: 'Financiamento sob medida para',
      heroHighlight: 'seu negócio',
      heroDesc: 'Relacionamento com mais de 200 instituições entre bancos, fundos, FIDCs, credores e investidores para atender qualquer demanda por recursos.',
      ctaPrimary: 'Solicitar Proposta',
      statsPartners: 'Instituições parceiras',
      statsOperations: 'Em operações estruturadas',
      statsAgents: 'Agentes em todo Brasil',
      statsResponse: 'Tempo médio de resposta',
      typesTag: 'Modalidades',
      typesTitle: 'Tipos de Crédito',
      typesDesc: 'Oferecemos diversas modalidades de crédito para atender às necessidades específicas do seu negócio.',
      type1: 'Capital de Giro',
      type1Desc: 'Recursos para manter as operações do dia a dia com fluxo de caixa saudável.',
      type2: 'Garantia de Recebíveis',
      type2Desc: 'Antecipação de valores a receber com condições especiais.',
      type3: 'Garantia de Veículos',
      type3Desc: 'Financiamento utilizando a frota da empresa como garantia.',
      type4: 'Máquinas e Equipamentos',
      type4Desc: 'Crédito para aquisição ou renovação de equipamentos industriais.',
      type5: 'Operação de Mezanino',
      type5Desc: 'Financiamento híbrido entre dívida e equity para crescimento.',
      type6: 'Incentivos Estatais',
      type6Desc: 'Acesso a linhas de crédito subsidiadas por programas governamentais.',
      processTag: 'Como Funciona',
      processTitle: 'Processo Simplificado',
      process1: 'Análise de Necessidades',
      process1Desc: 'Entendemos sua demanda e estruturamos a melhor solução.',
      process2: 'Busca de Parceiros',
      process2Desc: 'Acionamos nossa rede de 200+ instituições para encontrar as melhores condições.',
      process3: 'Negociação',
      process3Desc: 'Negociamos taxas e condições em seu nome.',
      process4: 'Liberação',
      process4Desc: 'Acompanhamos todo o processo até a liberação dos recursos.',
      whyChooseTitle: 'Por que escolher a Holder & Co.?',
      whyChoose1: 'Acesso a mais de 200 instituições financeiras',
      whyChoose2: 'Taxas competitivas negociadas',
      whyChoose3: 'Processo simplificado e ágil',
      whyChoose4: 'Assessoria completa na documentação',
      whyChoose5: 'Estruturação personalizada',
      whyChoose6: 'Acompanhamento até a liberação',
      networkTag: 'Nossa Rede',
      networkTitle: 'Acesso às Melhores Instituições',
      banks: 'Bancos',
      banksDesc: 'Grandes bancos e instituições financeiras',
      funds: 'Fundos',
      fundsDesc: 'FIDCs e fundos de investimento',
      creditors: 'Credores',
      creditorsDesc: 'Credores privados e family offices',
      investors: 'Investidores',
      investorsDesc: 'Investidores estratégicos',
      ctaTitle: 'Precisa de Crédito?',
      ctaDesc: 'Entre em contato e receba uma proposta personalizada em até 48 horas.',
      ctaBtn: 'Solicitar Proposta',
    },
    lawyerPage: {
      heroTag: 'Holder Law + Campos Feijó',
      heroTitle: 'Assessoria Jurídica',
      heroHighlight: 'Empresarial',
      heroDesc: 'Da união entre HOLDER & CO. e CAMPOS FEIJÓ nasce a HOLDER LAW com uma das maiores capilaridades jurídicas do país.',
      heroSubtitle: 'Soluções jurídicas completas e estratégicas para o ambiente empresarial.',
      ctaPrimary: 'Agendar Consulta',
      statsExperience: 'Anos de experiência',
      statsCases: 'Casos atendidos',
      statsSuccess: 'Taxa de sucesso',
      statsResponse: 'Tempo de resposta',
      partnershipTag: 'Parceria Estratégica',
      partnershipTitle: 'HOLDER LAW + CAMPOS FEIJÓ',
      partnershipDesc: 'Soluções jurídicas completas e estratégicas para o ambiente empresarial.',
      competence1: 'Escritório com mais de 20 anos de experiência e atendimento personalizado ao cliente empresarial.',
      competence2: 'Expertise em Direito Tributário: planejamento, benefícios fiscais, regimes especiais, defesa em processos e parcelamentos.',
      competence3: 'Atuação em Planejamento Societário, Revisão Fiscal e reorganização societária com foco em redução de custos e riscos.',
      competence4: 'Assessoria completa em Direito Cível, Empresarial, Trabalhista, Criminal e Ambiental.',
      competence5: 'Atuação destacada para os setores de Refeição Coletiva, Venda Direta, AgroBusiness, Gás e Petróleo.',
      competence6: 'Equipe multidisciplinar reconhecida pela ética, técnica e credibilidade.',
      servicesTag: 'Serviços Jurídicos',
      servicesTitle: 'Áreas de Atuação',
      servicesDesc: 'Oferecemos assessoria jurídica completa para empresas de todos os portes e segmentos.',
      service1: 'Contencioso Tributário',
      service1Desc: 'Defesa em processos administrativos e judiciais tributários, com estratégias para redução de passivos.',
      service2: 'Planejamento Tributário',
      service2Desc: 'Estruturação de operações para otimização da carga tributária dentro da legalidade.',
      service3: 'Direito Societário',
      service3Desc: 'Assessoria em constituição, reorganização societária, fusões, cisões e incorporações.',
      service4: 'Contratos Empresariais',
      service4Desc: 'Elaboração, revisão e negociação de contratos comerciais e empresariais.',
      service5: 'Compliance e LGPD',
      service5Desc: 'Implementação de programas de compliance e adequação à Lei Geral de Proteção de Dados.',
      service6: 'Recuperação de Empresas',
      service6Desc: 'Assessoria em processos de recuperação judicial e extrajudicial.',
      areasTag: 'Especialidades',
      areasTitle: 'Direito Empresarial Completo',
      areasDesc: 'Nossa equipe jurídica possui expertise em diversas áreas do direito empresarial, oferecendo suporte integral para todas as necessidades legais da sua empresa.',
      areaTax: 'Tributário',
      areaTaxDesc: 'Federal, Estadual e Municipal',
      areaCorporate: 'Empresarial',
      areaCorporateDesc: 'Societário e Contratos',
      areaLabor: 'Trabalhista',
      areaLaborDesc: 'Consultivo e Contencioso',
      areaCivil: 'Cível',
      areaCivilDesc: 'Contratos e Responsabilidade',
      areaCriminal: 'Criminal',
      areaCriminalDesc: 'Empresarial e Compliance',
      areaEnvironmental: 'Ambiental',
      areaEnvironmentalDesc: 'Licenciamento e Conformidade',
      sectorsTag: 'Setores de Atuação',
      sectorsTitle: 'Expertise Setorial',
      sectorsDesc: 'Atuação destacada em setores estratégicos da economia.',
      sectorFood: 'Refeição Coletiva',
      sectorDirectSales: 'Venda Direta',
      sectorAgro: 'AgroBusiness',
      sectorOilGas: 'Gás e Petróleo',
      sectorPharma: 'Farmacêutico',
      sectorTech: 'Tecnologia',
      trackRecordTag: 'Track Record',
      trackRecordTitle: 'Clientes que Confiam em Nosso Trabalho',
      trackRecordDesc: 'Empresas de diversos segmentos que contam com nossa assessoria jurídica estratégica.',
      whyChooseTitle: 'Por que escolher a Holder Law?',
      whyChoose1: 'Mais de 20 anos de experiência em direito empresarial',
      whyChoose2: 'Equipe multidisciplinar integrada',
      whyChoose3: 'Atuação preventiva e estratégica',
      whyChoose4: 'Conhecimento profundo do mercado financeiro',
      whyChoose5: 'Atendimento personalizado',
      whyChoose6: 'Resultados comprovados em negociações tributárias',
      approachTag: 'Nossa Abordagem',
      approachTitle: 'Atuação Preventiva e Estratégica',
      prevention: 'Prevenção',
      preventionDesc: 'Identificamos riscos antes que se tornem problemas, protegendo sua empresa de litígios desnecessários.',
      strategy: 'Estratégia',
      strategyDesc: 'Desenvolvemos estratégias jurídicas alinhadas aos objetivos de negócio da sua empresa.',
      results: 'Resultados',
      resultsDesc: 'Focamos em soluções práticas que geram valor real para o seu negócio.',
      ctaTitle: 'Precisa de Assessoria Jurídica?',
      ctaDesc: 'Agende uma consulta com nossa equipe e descubra como podemos proteger e impulsionar seu negócio.',
      ctaBtn: 'Agendar Consulta',
    },
  },
  en: {
    nav: {
      home: 'Home',
      about: 'About',
      services: 'Services',
      partners: 'Partners',
      contact: 'Contact',
      allServices: 'All Services',
      access: 'Access',
      contactUs: 'Contact Us',
    },
    services: {
      fiscalManagement: 'Digital Tax Management',
      fiscalManagementDesc: 'Tax credit identification and tax liability regularization.',
      positiveTax: 'Positive Tax Management',
      positiveTaxDesc: 'Tax savings through financial credit rights.',
      liabilities: 'Tax Liability Management',
      liabilitiesDesc: 'Tax regularization with discount negotiation and installment plans.',
      ma: 'M&A - Mergers & Acquisitions',
      maDesc: 'Full advisory services for company purchases, sales, and mergers.',
      credit: 'Structured Credit',
      creditDesc: 'Customized financing to meet specific business demands.',
      legal: 'Corporate Legal Advisory',
      legalDesc: 'Strategic legal support to protect assets and ensure compliance.',
    },
    home: {
      heroTag: 'Corporate Solutions Boutique',
      heroTitle: 'Intelligent solutions to',
      heroHighlight: 'transform',
      heroTitleEnd: 'your business',
      heroDesc: 'An ecosystem of integrated solutions to finance operations, manage taxes, optimize people, and transform businesses.',
      scheduleBtn: 'Schedule a Meeting',
      ourServices: 'Our Services',
      statsAssets: 'In managed assets',
      statsPartners: 'Partner institutions',
      statsAgents: 'Agents across Brazil',
      statsExperience: 'Years of experience',
      aboutUs: 'About Us',
      aboutTitle: 'Experts in financial and business development',
      aboutDesc: 'Holder & Co. Group is a corporate solutions boutique founded by professionals with over 20 years of market expertise. Our integrated ecosystem offers everything from strategic consulting to digital financial solutions.',
      aboutCredits: 'Over R$ 1.2 billion in identified tax credits',
      aboutCompliance: 'Full compliance with LGPD and regulatory standards',
      aboutNational: 'Nationwide coverage with specialized team',
      knowHistory: 'Learn Our Story',
      servicesTag: 'Our Services',
      servicesTitle: 'Complete solutions for your company',
      servicesDesc: 'From strategic planning to execution, we offer a comprehensive portfolio of services to drive your business growth.',
      learnMore: 'Learn more',
      viewAll: 'View All Services',
      trustedBy: 'COMPANIES THAT TRUST OUR WORK',
      mediaTag: 'Follow Us',
      mediaTitle: 'Holder & CO. in the Media',
      mediaDesc: 'Stay up to date with news, exclusive content, and media appearances.',
      followInstagram: 'Follow us on Instagram',
      youtubeTitle: 'Featured on YouTube',
      youtubeVideo1: 'HAS - Hold Account Service in the Media',
      youtubeVideo2: 'HAS - Digital Financial Solutions',
      ctaTag: "Let's Talk",
      ctaTitle: 'Ready to take the next step?',
      ctaDesc: 'Schedule a free meeting with our specialists and discover how we can help your company reach new heights.',
      ctaBtn: 'Schedule Free Meeting',
      yearsExp: 'Years of Experience',
      unitsInBrazil: 'Units in Brazil',
    },
    footer: {
      ctaTitle: 'Ready to transform your business?',
      ctaDesc: 'Schedule a meeting with our specialists and discover how we can help your company grow.',
      ctaBtn: 'Schedule Meeting',
      about: 'Corporate solutions boutique specialized in financial and business development.',
      services: 'Services',
      quickLinks: 'Quick Links',
      contact: 'Contact',
      phone: 'Phone',
      email: 'Email',
      offices: 'Our Offices',
      rights: 'All rights reserved.',
      compliance: 'In compliance with LGPD Law No. 13.709/2018',
      aboutUs: 'About Us',
      partnerProgram: 'Partner Program',
    },
    about: {
      historyTag: 'Our History',
      historyTitle: 'Building the financial future since',
      historyDesc1: 'Holder & Co. Group was born with the purpose of democratizing access to high-level financial solutions. We are a corporate solutions boutique that combines market experience with technological innovation.',
      historyDesc2: 'Our team of specialists has over 20 years of expertise in the financial market, having participated in the evaluation, recovery, renegotiation, fundraising, and management of over R$ 2 billion in assets and resources.',
      missionTitle: 'Our Mission',
      missionDesc: 'To promote and intermediate the contracting of digital payment products and services, optimizing financial management and transactions for corporate clients. To provide solid and sustainable results, delivering personalized solutions focused on each client\'s success.',
      visionTitle: 'Our Vision',
      visionDesc: 'To be recognized as the leading reference in integrated corporate solutions in Brazil, transforming how companies manage their finances, taxes, and strategic operations, always with excellence and innovation.',
      valuesTag: 'What Guides Us',
      valuesTitle: 'Our Values',
      excellence: 'Excellence',
      excellenceDesc: 'Commitment to the highest quality standards in everything we do.',
      innovation: 'Innovation',
      innovationDesc: 'Constant pursuit of creative solutions and cutting-edge technologies.',
      security: 'Security',
      securityDesc: 'Complete protection of our clients\' data and operations.',
      compliance: 'Compliance',
      complianceDesc: 'Full compliance with laws and regulations.',
      clientFocus: 'Client Focus',
      clientFocusDesc: 'Customized solutions for each client\'s needs.',
      ecosystemTag: 'Our Ecosystem',
      ecosystemTitle: 'Two Brands, One Complete Solution',
      holderBrand: 'Strategic Consulting',
      hasBrand: 'Hold Account Service - Digital Financial Solutions',
      teamTag: 'Who We Are',
      teamTitle: 'Our Team',
      teamDesc: 'Experienced professionals committed to excellence in serving our clients.',
      officesTag: 'National Presence',
      officesTitle: 'Our Offices',
      officesDesc: 'With strategically located offices, we are ready to serve companies throughout the country.',
      agentsInfo: 'Over 100 Holder Agents across Brazil',
      ctaTitle: 'Be part of our story',
      ctaDesc: 'Discover how we can help your company reach new heights.',
      ctaBtn: 'Contact Us',
      holderServices: {
        credit: 'Structured Credit',
        ma: 'Mergers & Acquisitions (M&A)',
        consulting: 'Management & Technology Consulting',
        fiscal: 'Tax & Accounting Management',
        legal: 'Corporate Legal Advisory',
      },
      hasServices: {
        accounts: 'Digital Accounts for Individuals & Businesses',
        escrow: 'Escrow Account',
        payments: 'Payment Solutions',
        cards: 'Card Machines',
        marketplace: 'Financial Marketplace',
      },
      team: {
        erico: {
          name: 'Erico Comassetto Sperb',
          role: 'Director of Institutional Relations',
          desc: 'Attorney and entrepreneur, specialist in Corporate Tax Law, with solid experience in network expansion and team coordination.',
        },
        nelson: {
          name: 'Nelson Gilberto Campos Feijo',
          role: 'Legal Director',
          desc: 'With over 30 years of experience in the legal field, marked by expertise in state-level tax administrative negotiations.',
        },
        renan: {
          name: 'Renan Mensch Scalzilli',
          role: 'Operations Director',
          desc: 'Business Administrator from UFRGS with emphasis on International Finance, with solid experience in stress management, turnaround, and M&A.',
        },
        paulo: {
          name: 'Paulo Cesar Pooch Ferreira',
          role: 'Technology Director',
          desc: 'Graduated in Business Administration from PUCRS. Solid experience in data analysis, financial analysis, and systems development.',
        },
        kamila: {
          name: 'Kamila Arpini Campos Feijo',
          role: 'Legal Analyst',
          desc: 'Specialist in legal support and tax process analysis.',
        },
        tyler: {
          name: 'Tyler Brito',
          role: 'Commercial Analyst',
          desc: 'Responsible for business development and client relationships.',
        },
      },
      offices: {
        matriz: 'Headquarters',
        unit: 'Branch',
      },
    },
    contact: {
      tag: 'Contact',
      title: "Let's",
      titleHighlight: 'talk',
      desc: 'Contact us to schedule a meeting or ask questions. Our team is ready to help your company.',
      infoTitle: 'Contact Information',
      phone: 'Phone',
      email: 'Email',
      hours: 'Business Hours',
      hoursValue: 'Monday to Friday: 9am to 6pm (BRT)',
      offices: 'Our Offices',
      social: 'Social Media',
      formTitle: 'Send a Message',
      formDesc: 'Fill out the form below and we will respond within 24 hours.',
      name: 'Full Name',
      emailField: 'Email',
      phoneField: 'Phone',
      company: 'Company',
      subject: 'Subject',
      subjectPlaceholder: 'Select a subject',
      message: 'Message',
      submit: 'Send Message',
      sending: 'Sending...',
      successTitle: 'Message Sent!',
      successDesc: 'Thank you for contacting us. Our team will respond shortly.',
      newMessage: 'Send New Message',
      privacy: 'By submitting, you agree to our privacy policy and data processing in accordance with LGPD.',
      errorTitle: 'Error sending',
      errorDesc: 'Please try again or contact us by phone.',
      subjects: {
        ma: 'M&A - Mergers & Acquisitions',
        fiscal: 'Digital Tax Management',
        positive: 'Positive Tax Management',
        liabilities: 'Liability Management',
        credit: 'Credit & Operations',
        partners: 'Partner Program',
        legal: 'Legal Advisory',
        other: 'Other',
      },
    },
    partners: {
      tag: 'Partner Program',
      title: 'Become a',
      titleHighlight: 'Holder Agent',
      desc: 'Join a network of over 100 agents across Brazil. Refer clients, close deals, and receive attractive commissions in a transparent and meritocratic model.',
      cta: 'I Want to Be a Partner',
      tiersTag: 'Partnership Tiers',
      tiersTitle: 'Choose Your Tier',
      adhesion: 'Enrollment',
      free: 'Free',
      optional: '(optional)',
      mostPopular: 'Most Popular',
      benefit1Bronze: 'Access to partner program',
      benefit2Bronze: 'Base commission',
      benefit3Bronze: 'Commercial support',
      benefit4Bronze: 'Sales materials',
      benefit1Silver: 'All Bronze benefits',
      benefit2Silver: 'Enhanced commission',
      benefit3Silver: 'Priority support',
      benefit4Silver: 'Exclusive training',
      benefit1Gold: 'All Silver benefits',
      benefit2Gold: 'Maximum commission',
      benefit3Gold: 'Dedicated account manager',
      benefit4Gold: 'VIP events',
      wantToBe: 'I Want to Be',
      ascensionTag: 'Growth',
      ascensionTitle: 'How to Move Up Tiers',
      rule1Title: 'Simple Referral',
      rule1Desc: 'Refer 5 Bronze partners who close deals to move up to Silver. For Gold, refer 5 Silver partners.',
      rule2Title: 'Tier Purchase',
      rule2Desc: 'Accelerate your tier upgrade by purchasing the corresponding enrollment (Silver: R$ 50k, Gold: R$ 100k).',
      rule3Title: 'Own Revenue',
      rule3Desc: 'Bronze to Silver: R$ 100k in sales. Silver to Gold: R$ 500k in sales.',
      ctaTitle: 'Ready to get started?',
      ctaDesc: 'Contact us and start your journey as a Holder Agent today.',
    },
    access: {
      title: 'Choose Portal',
      portalHolder: 'Holder Portal',
      portalHolderDesc: 'Access the client portal',
      hasBanking: 'HAS Internet Banking',
      hasBankingDesc: 'Access your digital account',
    },
    servicesPage: {
      heroTag: 'Our Services',
      heroTitle: 'Complete solutions for',
      heroHighlight: 'every need',
      heroDesc: 'An ecosystem of integrated solutions that combines high-level strategic consulting with cutting-edge financial technology.',
      highlightsTag: 'Highlights',
      highlightsTitle: 'Specialized Services',
      viewDetails: 'View details',
      consultingTag: 'Holder & Co.',
      consultingTitle: 'Strategic Consulting',
      digitalTag: 'HAS - Hold Account Service',
      digitalTitle: 'Digital Financial Solutions',
      techTag: 'Technology',
      techTitle: 'Advanced Technological Integration',
      techDesc: 'We offer robust APIs for integration with enterprise management systems, fintechs, and partner platforms, enabling automation of financial processes and greater operational efficiency.',
      techFeature1: 'High-performance Financial APIs',
      techFeature2: 'Compatibility with major market ERPs',
      techFeature3: 'Integrated solutions ecosystem',
      techFeature4: 'Financial process automation',
      techCode: 'Complete documentation and dedicated technical support for your integration.',
      ctaTitle: "Didn't find what you're looking for?",
      ctaDesc: 'We develop custom projects to meet specific needs of your business.',
      ctaBtn: 'Talk to a Specialist',
      consulting: {
        management: 'Management & Technology Consulting',
        managementDesc: 'Implementation of solutions that optimize processes and drive business growth.',
        accounting: 'Accounting Management',
        accountingDesc: 'Planning and control to maximize tax and accounting efficiency.',
        legal: 'Corporate Legal Advisory',
        legalDesc: 'Strategic legal support to protect assets and ensure compliance.',
        thirdSector: 'Third Sector Solutions',
        thirdSectorDesc: 'Specialized consulting in social impact solutions through non-profit organizations.',
      },
      digital: {
        accounts: 'Digital Accounts',
        accountsDesc: 'Complete account with debit and credit card for individuals and businesses.',
        escrow: 'Escrow Account',
        escrowDesc: 'Escrow account for secure transactions with conditional release.',
        payments: 'Payment Solutions',
        paymentsDesc: 'Cards, bank slips, transfers, digital wallets, and payment splits.',
        baas: 'Banking as a Service (BaaS)',
        baasDesc: 'Flexibility to offer financial products without proprietary banking infrastructure.',
        boletos: 'Bank Slip Issuance',
        boletosDesc: 'Integrated system for generation and control of bank slips.',
        transfers: 'Transfers (Wire, Instant)',
        transfersDesc: 'Fast and secure solutions for financial transfers.',
        exchange: 'Foreign Exchange',
        exchangeDesc: 'Access to the best exchange rates for international operations.',
        creditFinancing: 'Credit & Financing',
        creditFinancingDesc: 'Financing lines and loans with special conditions.',
        insurance: 'Insurance & Consortiums',
        insuranceDesc: 'Protection solutions and alternatives for asset acquisition.',
      },
    },
    common: {
      learnMore: 'Learn more',
      scheduleMeeting: 'Schedule Meeting',
      contactUs: 'Contact Us',
      viewAll: 'View All',
    },
    maPage: {
      heroTag: 'Mergers & Acquisitions',
      heroTitle: 'Complete advisory in',
      heroHighlight: 'M&A',
      heroDesc: 'We conduct the entire sale process, from conception to materiality, with expertise in valuation, due diligence, and strategic negotiation.',
      ctaPrimary: 'Schedule Free Assessment',
      statsOperations: 'In advised operations',
      statsPartners: 'Partner institutions',
      statsProjects: 'Completed projects',
      statsExperience: 'Years of experience',
      methodologyTag: 'Methodology',
      methodologyTitle: 'Our M&A Process',
      step1: 'Teaser',
      step1Desc: 'Initial business presentation to potential investors.',
      step2: 'Scenario Assessment',
      step2Desc: 'Strategic analysis of options and possible paths.',
      step3: 'Negotiation',
      step3Desc: 'Conducting negotiations with qualified buyers.',
      step4: 'Fundraising',
      step4Desc: 'Active search for investors and deal closing.',
      servicesTag: 'M&A Services',
      servicesTitle: 'Complete Advisory',
      service1: 'Approaching potential Buyers',
      service2: 'Non-Disclosure Agreement (NDA)',
      service3: 'Negotiation and Due Diligence',
      service4: 'Letter of Intent (LOI)',
      service5: 'SPA (Share Purchase Agreement)',
      service6: 'Closing and Transfer',
      valuationTag: 'Valuation',
      valuationTitle: 'Economic-Financial Valuation',
      valuationDesc: 'We use market practices and a rigorous methodological process to perform the financial valuation of your business.',
      valuationStepsTitle: 'Valuation Steps',
      valuationStep1: 'Internal Diagnosis',
      valuationStep2: 'External Diagnosis',
      valuationStep3: 'Financial Projections',
      valuationStep4: 'Needs Assessment',
      valuationStep5: 'Final Value',
      creditTag: 'Credit & Operations',
      creditTitle: 'Structured Operations',
      creditDesc: 'Relationships with over 200 institutions including banks, funds, FIDCs, creditors, and investors to meet any resource demand.',
      credit1: 'Working Capital',
      credit1Desc: 'Resources for daily operations',
      credit2: 'Receivables-Backed',
      credit2Desc: 'Credit backed by receivables',
      credit3: 'Vehicle-Backed',
      credit3Desc: 'Financing with fleet as collateral',
      credit4: 'Machinery & Equipment',
      credit4Desc: 'Credit for asset acquisition',
      credit5: 'Mezzanine Operation',
      credit5Desc: 'Hybrid financing',
      credit6: 'Government Incentives',
      credit6Desc: 'Access to subsidized credit lines',
      networkTag: 'Our Network',
      networkTitle: 'Access to Qualified Investors',
      networkDesc: 'We develop the Investment Thesis for your business and prospect investors through our exclusive network, which includes hundreds of strategic investors, private equity funds, venture capital, and family offices.',
      privateEquity: 'Private Equity',
      ventureCapital: 'Venture Capital',
      familyOffices: 'Family Offices',
      strategicInvestors: 'Strategic Investors',
      diagnosisTitle: 'Free Assessment',
      diagnosisDesc: 'Schedule a meeting for a free assessment of your business value potential.',
      diagnosisBtn: 'Schedule Assessment',
      ctaTitle: 'Ready to take the next step?',
      ctaDesc: 'Talk to our team and understand how we can help in your specific case.',
      ctaBtn: 'Schedule Meeting',
    },
    gfdPage: {
      heroTag: 'Digital Tax Management',
      heroTitle: 'Transform tax complexity into',
      heroHighlight: 'positive cash flow',
      heroDesc: 'A strategic investigation of the last 60 months of your company to identify hidden credits, map risks, and optimize your cash flow.',
      ctaPrimary: 'Request Assessment',
      statsCredits: 'In identified tax credits',
      statsMonths: 'Months of retroactive analysis',
      statsExperience: 'Years of experience',
      statsDays: 'Business days for delivery',
      approachTag: 'Our Approach',
      approachTitle: 'The Three Pillars of DTM',
      approachDesc: 'Digital Tax Management goes beyond traditional accounting, performing a complete x-ray of your tax situation.',
      pillar1: 'REVIEW',
      pillar1Sub: 'Hidden Assets',
      pillar1Desc: 'Federal, state, and municipal tax credits that can be administratively recovered.',
      pillar2: 'RECOVER',
      pillar2Sub: 'Cash Optimization',
      pillar2Desc: 'Legal alternatives to anticipate credits and reduce tax liabilities.',
      pillar3: 'REGULARIZE',
      pillar3Sub: 'Security (Liabilities)',
      pillar3Desc: 'Identification of filling errors and audit risks before the tax authorities find them.',
      methodologyTag: 'Methodology',
      methodologyTitle: 'The Path to Success in 6 Steps',
      method1: 'Digital Collection',
      method1Desc: 'Data capture via electronic power of attorney (e-CAC) or file sharing.',
      method2: 'Identification',
      method2Desc: 'Data cross-referencing using advanced software to detect inconsistencies.',
      method3: 'Mapping',
      method3Desc: 'Detailed analysis of tax impacts by specialized consultants.',
      method4: 'Technical Meeting',
      method4Desc: 'Discussion with the company team about identified points.',
      method5: 'Final Dossier',
      method5Desc: 'Presentation to the board with calculation records and decision support.',
      method6: 'Qualification',
      method6Desc: 'Technical guidance for asset recovery and necessary corrections.',
      feeTag: 'Contracting Model',
      feeTitle: 'Zero Risk (Success Fee)',
      feeDesc: 'Our compensation model is based exclusively on success. If no financial benefit is identified, there is no cost to you.',
      feeAssets: 'Fees on Assets',
      feeAssetsDesc: 'on identified tax benefits',
      feeLiabilities: 'Fees on Liabilities',
      feeLiabilitiesDesc: 'on mapped risks/liabilities',
      guaranteesTitle: 'Included Guarantees',
      guarantee1: 'No cost if no benefit is identified',
      guarantee2: 'Travel expenses covered by us',
      guarantee3: 'Delivery within 60 business days',
      guarantee4: 'Professional liability insurance',
      securityTag: 'Security',
      securityTitle: 'Security and Compliance',
      lgpd: 'LGPD',
      lgpdDesc: 'Data processing strictly for the contract purpose, with complete disposal after completion.',
      insurance: 'Professional Insurance',
      insuranceDesc: 'Professional liability policy with AKAD Insurance for complete peace of mind.',
      foundation: 'Foundation',
      foundationDesc: 'Reports based on current normative instructions and Federal Revenue rulings.',
      urgencyTitle: 'Time is Your Asset',
      urgencyDesc: 'Tax authorities have a 5-year deadline to analyze. Each passing month represents a credit that may expire.',
      urgencyBtn: 'Request Analysis Now',
    },
    gptPage: {
      heroTag: 'Positive Tax Management',
      heroTitle: 'Tax savings through',
      heroHighlight: 'credit rights',
      heroDesc: 'Use credits from final court decisions to settle federal taxes such as PIS-COFINS, CSLL, and IRPJ, unlocking up to 20% in recurring savings.',
      ctaPrimary: 'Schedule Meeting',
      accountingTitle: 'Accounting Treatment',
      asset: 'ASSET (100%)',
      assetDesc: 'Acquired Credit Rights',
      liability: 'LIABILITY (80%)',
      liabilityDesc: 'Accounts Payable',
      revenue: 'DEFERRED REVENUE (20%)',
      revenueDesc: 'Tax Savings',
      whatIsTag: 'What It Is',
      whatIsTitle: 'Financial Credit Rights (DCOF)',
      whatIsDesc: 'Credits from final court decisions against the Federal Government that can be acquired by the company and used to settle federal taxes.',
      whatIsHighlight: 'A tax strategy previously restricted to large players, now accessible to medium and large companies.',
      whatIsNotTitle: 'What Positive Management is NOT',
      notTax: 'Legal Thesis',
      notTaxDesc: 'Not based on questionable interpretations',
      notBenefit: 'Tax Benefit',
      notBenefitDesc: 'Not an exemption granted by the government',
      notCompensation: 'Traditional Offset',
      notCompensationDesc: 'Different from common offsets',
      notCredit: 'Tax Credit',
      notCreditDesc: 'It is a financial credit right',
      benefitsTag: 'Benefits',
      benefitsTitle: 'Advantages of Positive Management',
      benefit1: 'Recurring Savings',
      benefit1Desc: 'Up to 20% recurring savings on federal taxes.',
      benefit2: 'Legal Security',
      benefit2Desc: 'Credits with final judgment and specialized technical opinion.',
      benefit3: 'Cash Improvement',
      benefit3Desc: 'Direct cash injection into the company.',
      benefit4: 'Accounting Governance',
      benefit4Desc: 'Proper recording in balance sheet and EFD/ECF.',
      creditUsage: 'Use the credit over 10 months, according to your cash flow.',
      processTag: 'Step by Step',
      processTitle: 'How It Works',
      process1: 'DCOF Assignment',
      process1Desc: 'Right assignment contract signed by two witnesses.',
      process2: 'Filing',
      process2Desc: 'Immediate filing in the digital court process.',
      process3: 'Administrative Communication',
      process3Desc: 'Made in the 1st ancillary obligation (EFD) after assignment.',
      process4: 'Balance Sheet',
      process4Desc: 'Credit recorded in the taxpayer balance sheet.',
      process5: 'ECF Declaration',
      process5Desc: 'Credit uses declared in the annual ECF.',
      process6: 'Technical Responsibility',
      process6Desc: 'Technical responsibility for 5 years (legal, accounting, tax).',
      legalTag: 'Foundation',
      legalTitle: 'Legal Basis',
      legal1: 'EC No. 113/2021',
      legal1Date: 'December 8, 2021',
      legal1Desc: 'New precatory regime, allowing use of credits to settle debts with the Federal Government.',
      legal2: 'IN RFB No. 2055/2021',
      legal2Date: 'December 8, 2021',
      legal2Desc: 'Regulates refund, offset, and reimbursement of taxes administered by the Federal Revenue.',
      legal3: 'Decree No. 11.526/2023',
      legal3Date: 'May 12, 2023',
      legal3Desc: 'Adjusts procedures for greater legal security in credit offerings.',
      securityTag: 'Security',
      securityTitle: 'Integrated Legal-Accounting-Tax Approach',
      securityDesc: 'Our operation is conducted with full legal security, ensuring approval with PGFN and protection against deconstitution risks.',
      security1: 'Credits with final judgment',
      security2: 'Specialized technical opinion',
      security3: 'PGFN approval',
      security4: 'CPR guarantee for judicial defense',
      studyTitle: 'Personalized Case Study',
      studyDesc: 'Quantify the specific savings potential for your company and its impact on results.',
      studyBtn: 'Request Study',
      ctaTitle: 'Transform Your Tax Management',
      ctaDesc: 'Discover how tax savings through credit rights can optimize your tax management and financial performance.',
      ctaBtn: 'Schedule Meeting',
    },
    gpPage: {
      heroTag: 'Tax Liability Management',
      heroTitle: 'Tax regularization with',
      heroHighlight: 'significant discounts',
      heroDesc: 'Transform tax debts into opportunities through payment capacity review, negotiation of up to 70% discounts, and installment plans of up to 144 months.',
      ctaPrimary: 'Analyze My Debt',
      caseStudyTag: 'Real Case Study',
      caseInitialDebt: 'Initial Debt',
      caseInitialRating: 'Initial Rating',
      caseDiscount: 'Discount Obtained',
      caseFinalValue: 'Final Value',
      caseInstallments: 'Installments of only R$ 4,000/month over 10 years',
      ratingTag: 'Rating System',
      ratingTitle: 'Lower Rating Means Higher Discount',
      ratingDesc: 'The rating determines negotiation conditions. Our job is to analyze your real payment capacity and seek reclassification to obtain better conditions.',
      ratingA: 'Very high rating',
      ratingB: 'Moderate discount',
      ratingC: 'Significant discount',
      ratingD: 'Maximum discount',
      installmentsInfo: 'Installments up to 144 months',
      processTag: 'Methodology',
      processTitle: 'Our Regularization Process',
      process1: 'CAPAG Analysis',
      process1Desc: 'Assessment of payment capacity calculated by tax authorities vs. company reality.',
      process2: 'Review Report',
      process2Desc: 'Preparation of technical report for payment capacity review.',
      process3: 'PGFN Filing',
      process3Desc: 'Request submission to the Attorney General of the National Treasury.',
      process4: 'Negotiation',
      process4Desc: 'Conducting negotiations to obtain better installment conditions.',
      benefitsTag: 'Benefits',
      benefitsTitle: 'Why Regularize Your Liabilities?',
      benefit1: 'Significant reduction in total debt value',
      benefit2: 'More accessible and flexible payment conditions',
      benefit3: 'Cash flow release for operations',
      benefit4: 'Avoids sanctions and possible blocks',
      benefit5: 'Complete tax regularization',
      legalBasisTitle: 'Legal Basis',
      legalBasisDesc: 'Our practice is based on PGFN Ordinance No. 6.757/2022, which establishes the premises for tax debt negotiation.',
      legalBasisNote: 'The payment capacity calculated by tax authorities may be higher than your company reality. Our technical report demonstrates your real situation.',
      comparisonTag: 'Comparison',
      comparisonTitle: 'Before and After Regularization',
      withoutTitle: 'Without Liability Management',
      without1: 'Full debt without discounts',
      without2: 'High installments',
      without3: 'Risk of sanctions and blocks',
      without4: 'Cash flow compromise',
      without5: 'Difficulty obtaining credit',
      withTitle: 'With Liability Management',
      with1: 'Up to 70% discount on debt',
      with2: 'Installments suited to capacity',
      with3: 'Complete tax regularization',
      with4: 'Cash flow release',
      with5: 'Improved credit analysis',
      ctaTitle: 'Discover Your Discount Potential',
      ctaDesc: 'We analyze your tax situation and identify the best negotiation opportunities for your company.',
      ctaBtn: 'Request Free Analysis',
    },
    creditPage: {
      heroTag: 'Structured Credit',
      heroTitle: 'Tailored financing for',
      heroHighlight: 'your business',
      heroDesc: 'Relationships with over 200 institutions including banks, funds, FIDCs, creditors, and investors to meet any resource demand.',
      ctaPrimary: 'Request Proposal',
      statsPartners: 'Partner institutions',
      statsOperations: 'In structured operations',
      statsAgents: 'Agents across Brazil',
      statsResponse: 'Average response time',
      typesTag: 'Modalities',
      typesTitle: 'Credit Types',
      typesDesc: 'We offer various credit modalities to meet the specific needs of your business.',
      type1: 'Working Capital',
      type1Desc: 'Resources to maintain day-to-day operations with healthy cash flow.',
      type2: 'Receivables-Backed',
      type2Desc: 'Advance of receivables with special conditions.',
      type3: 'Vehicle-Backed',
      type3Desc: 'Financing using the company fleet as collateral.',
      type4: 'Machinery & Equipment',
      type4Desc: 'Credit for acquisition or renewal of industrial equipment.',
      type5: 'Mezzanine Operation',
      type5Desc: 'Hybrid financing between debt and equity for growth.',
      type6: 'Government Incentives',
      type6Desc: 'Access to subsidized credit lines through government programs.',
      processTag: 'How It Works',
      processTitle: 'Simplified Process',
      process1: 'Needs Analysis',
      process1Desc: 'We understand your demand and structure the best solution.',
      process2: 'Partner Search',
      process2Desc: 'We activate our 200+ institution network to find the best conditions.',
      process3: 'Negotiation',
      process3Desc: 'We negotiate rates and conditions on your behalf.',
      process4: 'Release',
      process4Desc: 'We follow the entire process until resource release.',
      whyChooseTitle: 'Why choose Holder & Co.?',
      whyChoose1: 'Access to over 200 financial institutions',
      whyChoose2: 'Negotiated competitive rates',
      whyChoose3: 'Simplified and agile process',
      whyChoose4: 'Complete documentation advisory',
      whyChoose5: 'Personalized structuring',
      whyChoose6: 'Monitoring until release',
      networkTag: 'Our Network',
      networkTitle: 'Access to the Best Institutions',
      banks: 'Banks',
      banksDesc: 'Major banks and financial institutions',
      funds: 'Funds',
      fundsDesc: 'FIDCs and investment funds',
      creditors: 'Creditors',
      creditorsDesc: 'Private creditors and family offices',
      investors: 'Investors',
      investorsDesc: 'Strategic investors',
      ctaTitle: 'Need Credit?',
      ctaDesc: 'Contact us and receive a personalized proposal within 48 hours.',
      ctaBtn: 'Request Proposal',
    },
    lawyerPage: {
      heroTag: 'Holder Law + Campos Feijo',
      heroTitle: 'Corporate Legal',
      heroHighlight: 'Advisory',
      heroDesc: 'From the partnership between HOLDER & CO. and CAMPOS FEIJO, HOLDER LAW emerges with one of the largest legal networks in the country.',
      heroSubtitle: 'Complete and strategic legal solutions for the business environment.',
      ctaPrimary: 'Schedule Consultation',
      statsExperience: 'Years of experience',
      statsCases: 'Cases handled',
      statsSuccess: 'Success rate',
      statsResponse: 'Response time',
      partnershipTag: 'Strategic Partnership',
      partnershipTitle: 'HOLDER LAW + CAMPOS FEIJO',
      partnershipDesc: 'Complete and strategic legal solutions for the business environment.',
      competence1: 'Law firm with over 20 years of experience and personalized service for corporate clients.',
      competence2: 'Expertise in Tax Law: planning, tax benefits, special regimes, defense in proceedings and installment plans.',
      competence3: 'Practice in Corporate Planning, Tax Review and corporate reorganization focused on cost and risk reduction.',
      competence4: 'Complete advisory in Civil, Corporate, Labor, Criminal and Environmental Law.',
      competence5: 'Outstanding practice in Corporate Food Services, Direct Sales, AgroBusiness, Oil & Gas sectors.',
      competence6: 'Multidisciplinary team recognized for ethics, technical expertise and credibility.',
      servicesTag: 'Legal Services',
      servicesTitle: 'Practice Areas',
      servicesDesc: 'We offer complete legal advisory for companies of all sizes and segments.',
      service1: 'Tax Litigation',
      service1Desc: 'Defense in administrative and judicial tax proceedings, with strategies to reduce liabilities.',
      service2: 'Tax Planning',
      service2Desc: 'Operation structuring for tax burden optimization within legality.',
      service3: 'Corporate Law',
      service3Desc: 'Advisory in incorporation, corporate reorganization, mergers, spin-offs, and acquisitions.',
      service4: 'Business Contracts',
      service4Desc: 'Drafting, review, and negotiation of commercial and business contracts.',
      service5: 'Compliance & LGPD',
      service5Desc: 'Implementation of compliance programs and adaptation to the General Data Protection Law.',
      service6: 'Business Recovery',
      service6Desc: 'Advisory in judicial and extrajudicial recovery processes.',
      areasTag: 'Specialties',
      areasTitle: 'Complete Business Law',
      areasDesc: 'Our legal team has expertise in various areas of business law, offering comprehensive support for all legal needs of your company.',
      areaTax: 'Tax',
      areaTaxDesc: 'Federal, State, and Municipal',
      areaCorporate: 'Corporate',
      areaCorporateDesc: 'Corporate and Contracts',
      areaLabor: 'Labor',
      areaLaborDesc: 'Advisory and Litigation',
      areaCivil: 'Civil',
      areaCivilDesc: 'Contracts and Liability',
      areaCriminal: 'Criminal',
      areaCriminalDesc: 'Corporate and Compliance',
      areaEnvironmental: 'Environmental',
      areaEnvironmentalDesc: 'Licensing and Compliance',
      sectorsTag: 'Industry Sectors',
      sectorsTitle: 'Sector Expertise',
      sectorsDesc: 'Outstanding practice in strategic economic sectors.',
      sectorFood: 'Corporate Food Services',
      sectorDirectSales: 'Direct Sales',
      sectorAgro: 'AgroBusiness',
      sectorOilGas: 'Oil & Gas',
      sectorPharma: 'Pharmaceutical',
      sectorTech: 'Technology',
      trackRecordTag: 'Track Record',
      trackRecordTitle: 'Clients Who Trust Our Work',
      trackRecordDesc: 'Companies from various segments that rely on our strategic legal advisory.',
      whyChooseTitle: 'Why choose Holder Law?',
      whyChoose1: 'Over 20 years of experience in business law',
      whyChoose2: 'Integrated multidisciplinary team',
      whyChoose3: 'Preventive and strategic practice',
      whyChoose4: 'Deep knowledge of the financial market',
      whyChoose5: 'Personalized service',
      whyChoose6: 'Proven results in tax negotiations',
      approachTag: 'Our Approach',
      approachTitle: 'Preventive and Strategic Practice',
      prevention: 'Prevention',
      preventionDesc: 'We identify risks before they become problems, protecting your company from unnecessary litigation.',
      strategy: 'Strategy',
      strategyDesc: 'We develop legal strategies aligned with your company business objectives.',
      results: 'Results',
      resultsDesc: 'We focus on practical solutions that generate real value for your business.',
      ctaTitle: 'Need Legal Advisory?',
      ctaDesc: 'Schedule a consultation with our team and discover how we can protect and drive your business.',
      ctaBtn: 'Schedule Consultation',
    },
  },
};

export default LanguageProvider;
